import { useCallback, useState } from 'react';
import { useURLState } from './useURLState';
import { useEnrichedData } from './useEnrichedData';
import { useStepValidation } from './useStepValidation';
import { useAutoClear } from './useAutoClear';

/**
 * Main composed hook for Risk Calculator
 * Combines URL state, enriched data, validation, and auto-clear logic
 *
 * This hook provides a unified API for the Risk Calculator feature,
 * replacing the monolithic useRiskCalculatorURLState hook.
 *
 * @returns {Object} Complete Risk Calculator state and actions
 */
export function useRiskCalculator() {
  // URL state management
  const {
    urlState,
    setUrlState,
    currentStep,
    currentView,
    selections,
    goToStep,
    goToNextStep,
    goToPreviousStep,
    setView,
    goToResults,
    goToForm,
    resetUrlState,
    isFirstStep,
    isLastStep,
    getShareableURL,
  } = useURLState();

  // Enriched data management
  const {
    enrichedData,
    isLoadingLabels,
    resetEnrichedData,
    updateEnrichedCultures,
    updateEnrichedTreatmentType,
    updateEnrichedApplicationTechnique,
    updateEnrichedProducts,
    clearDependentEnrichedData,
  } = useEnrichedData(urlState);

  // Step validation
  const {
    isStepValid,
    canProceedToNextStep,
    canCalculate,
  } = useStepValidation(selections, currentStep, setUrlState);

  // Auto-clear dependent selections
  useAutoClear(urlState, setUrlState, clearDependentEnrichedData);

  // Results state (not stored in URL)
  const [results, setResults] = useState(null);

  // Combined data actions with enriched data updates
  const setCultures = useCallback(
    (cultures, enrichedNodes = []) => {
      setUrlState({ cultures });
      if (enrichedNodes && enrichedNodes.length > 0) {
        updateEnrichedCultures(enrichedNodes);
      }
    },
    [setUrlState, updateEnrichedCultures]
  );

  const setTreatmentType = useCallback(
    (type, option = null) => {
      setUrlState({ treatmentType: type });
      if (option) {
        updateEnrichedTreatmentType({
          id: type,
          name: option.label || option.rawData?.name,
        });
      }
    },
    [setUrlState, updateEnrichedTreatmentType]
  );

  const setApplicationTechnique = useCallback(
    (technique, option = null) => {
      setUrlState({ applicationTechnique: technique });
      if (option) {
        updateEnrichedApplicationTechnique({
          id: technique,
          name: option.label || option.rawData?.name,
          abbreviation: option.rawData?.abbreviation || '',
        });
      }
    },
    [setUrlState, updateEnrichedApplicationTechnique]
  );

  const setCommercialProducts = useCallback(
    (products, enrichedProducts = null) => {
      setUrlState({ products });
      if (enrichedProducts && enrichedProducts.length > 0) {
        updateEnrichedProducts(enrichedProducts);
      }
    },
    [setUrlState, updateEnrichedProducts]
  );

  // Full reset
  const reset = useCallback(() => {
    resetUrlState();
    resetEnrichedData();
    setResults(null);
  }, [resetUrlState, resetEnrichedData]);

  return {
    // State
    currentStep,
    currentView,
    selections,
    enrichedData,
    results,
    isLoadingLabels,

    // Navigation actions
    goToStep,
    goToNextStep,
    goToPreviousStep,

    // View actions
    setView,
    goToResults,
    goToForm,

    // Data actions
    setCultures,
    setTreatmentType,
    setApplicationTechnique,
    setCommercialProducts,
    setResults,
    reset,

    // Validation
    isStepValid,
    canProceedToNextStep,
    canCalculate,

    // Helper flags
    isFirstStep,
    isLastStep,

    // URL sharing
    getShareableURL,
  };
}

export default useRiskCalculator;
